import clamp from 'clamp';
import { isPlainObject } from 'is-plain-object';

import { Logger } from '@gitbook/util-debug';
import { Set as ISet, Record } from '@gitbook/util-immutable';

import { isBlock, isInline, isTextRange, MODEL_TYPES } from '../constants';
import type { Block } from './block';
import type { BlockRange } from './block-range';
import { Data } from './data';
import type { Document } from './document';
import type { Fragment } from './fragment';
import type { Inline } from './inline';
import { Mark, type MarkCreateAttrs } from './mark';
import type { NodeType } from './node-serialization';
import type { Text } from './text';
import type { SelectionRange } from './value';

const logger = Logger('slate:text-range');

export interface TextRangeProperties {
    anchorKey: string | null;
    anchorOffset: number;
    focusKey: string | null;
    focusOffset: number;
    isBackward: boolean | null;
    isFocused: boolean | null;
    marks: ISet<Mark> | null;
    isAtomic: boolean;
    data: Data;
}

export type TextRangeCreateProps =
    | TextRange
    | Partial<
          Omit<TextRangeProperties, 'marks' | 'data'> & {
              marks: MarkCreateAttrs[] | null;
              data: object | Data;
          }
      >;

export type TextRangeCreateSetProps = ISet<TextRange> | TextRangeCreateProps[];

/*
 * TextRange in the document (used for selection, decoration with marks, etc)
 */
export class TextRange extends Record<TextRangeProperties>({
    anchorKey: null,
    anchorOffset: 0,
    focusKey: null,
    focusOffset: 0,
    isBackward: null,
    isFocused: false,
    marks: null,
    isAtomic: false,
    data: Data.create(),
}) {
    // Record properties
    declare public readonly anchorKey: string | null;
    declare public readonly anchorOffset: number;
    declare public readonly focusKey: string | null;
    declare public readonly focusOffset: number;
    declare public readonly isBackward: boolean | null;
    declare public readonly isFocused: boolean | null;
    declare public readonly marks: ISet<Mark> | null;
    declare public readonly isAtomic: boolean;
    declare public readonly data: Data;

    /*
     * Object.
     */

    get object(): 'range' {
        return 'range';
    }

    /*
     * Check whether the range is blurred.
     */
    get isBlurred(): boolean {
        return !this.isFocused;
    }

    /*
     * Check whether the range is collapsed.
     */
    get isCollapsed(): boolean {
        return this.anchorKey === this.focusKey && this.anchorOffset === this.focusOffset;
    }

    /*
     * Check whether the range is expanded.
     */
    get isExpanded(): boolean {
        return !this.isCollapsed;
    }

    /*
     * Check whether the range is forward.
     */
    get isForward(): boolean {
        return (this.isBackward == null ? null : !this.isBackward) as boolean;
    }

    /**
     * Check whether the range's keys are set.
     */
    get isSet(): boolean {
        return this.anchorKey != null && this.focusKey != null;
    }

    /**
     * Check whether the range's keys are not set.
     */
    get isUnset(): boolean {
        return !this.isSet;
    }

    /*
     * Get the start key.
     */
    get startKey(): string | null {
        return this.isBackward ? this.focusKey : this.anchorKey;
    }

    /*
     * Get the start offset.
     */
    get startOffset(): number {
        return this.isBackward ? this.focusOffset : this.anchorOffset;
    }

    /*
     * Get the end key.
     */

    get endKey(): string | null {
        return this.isBackward ? this.anchorKey : this.focusKey;
    }

    /*
     * Get the end offset.
     */
    get endOffset(): number {
        return this.isBackward ? this.anchorOffset : this.focusOffset;
    }

    /*
     * Create a new `TextRange` with `attrs`.
     */
    public static create(attrs: TextRangeCreateProps = {}): TextRange {
        if (TextRange.isTextRange(attrs)) {
            return attrs;
        }

        return TextRange.createRecord({
            ...attrs,
            data: Data.create(attrs.data || {}),
            marks: attrs.marks ? Mark.createSet(attrs.marks) : null,
        });
    }

    /*
     * Create a set of ranges.
     */
    public static createSet(elements?: TextRangeCreateSetProps): ISet<TextRange> {
        if (!elements) {
            return ISet.create();
        }

        if (ISet.isSet(elements)) {
            return elements;
        }

        if (Array.isArray(elements)) {
            return ISet.create(elements.map((element) => TextRange.create(element)));
        }

        throw new Error(
            `\`TextRange.createSet\` only accepts sets, arrays or null, but you passed it: ${elements}`
        );
    }

    /*
     * Create a dictionary of settable range properties from `attrs`.
     *
     * @param {Object|String|TextRange} attrs
     * @return {Object}
     */

    public static createProperties(attrs: TextRangeCreateProps = {}): Partial<TextRangeProperties> {
        if (TextRange.isTextRange(attrs)) {
            return {
                anchorKey: attrs.anchorKey,
                anchorOffset: attrs.anchorOffset,
                focusKey: attrs.focusKey,
                focusOffset: attrs.focusOffset,
                isBackward: attrs.isBackward,
                isFocused: attrs.isFocused,
                marks: attrs.marks,
                isAtomic: attrs.isAtomic,
                data: attrs.data,
            };
        }

        if (isPlainObject(attrs)) {
            const props: Partial<TextRangeProperties> = {};
            if ('anchorKey' in attrs) {
                props.anchorKey = attrs.anchorKey;
            }
            if ('anchorOffset' in attrs) {
                props.anchorOffset = attrs.anchorOffset;
            }
            if ('focusKey' in attrs) {
                props.focusKey = attrs.focusKey;
            }
            if ('focusOffset' in attrs) {
                props.focusOffset = attrs.focusOffset;
            }
            if ('isBackward' in attrs) {
                props.isBackward = attrs.isBackward;
            }
            if ('isFocused' in attrs) {
                props.isFocused = attrs.isFocused;
            }
            if ('marks' in attrs) {
                props.marks = attrs.marks ? Mark.createSet(attrs.marks) : null;
            }
            if ('isAtomic' in attrs) {
                props.isAtomic = attrs.isAtomic;
            }
            if ('data' in attrs) {
                props.data = Data.create(attrs.data);
            }
            return props;
        }

        throw new Error(
            `\`TextRange.createProperties\` only accepts objects or ranges, but you passed it: ${attrs}`
        );
    }

    /*
     * Check if an `obj` is a `TextRange`.
     */
    public static isTextRange(obj: any): obj is TextRange {
        return isTextRange(obj);
    }

    /*
     * Create a new TextRange from a BlockRange.
     */
    public static fromBlockRange(blockRange: BlockRange, document: Document): TextRange {
        const anchorBlock = blockRange.anchorBlock
            ? document.assertBlock(blockRange.anchorBlock)
            : null;
        const focusBlock = blockRange.focusBlock
            ? document.assertBlock(blockRange.focusBlock)
            : null;
        const anchorText = anchorBlock?.getFirstText() ?? null;
        const focusText = focusBlock?.getLastText() ?? null;
        const anchorKey = anchorText?.key ?? null;
        const focusKey = focusText?.key ?? null;
        const isFocused = blockRange.isFocused;

        return TextRange.create({
            anchorKey,
            anchorOffset: 0,
            focusKey,
            focusOffset: focusText?.text.length ?? 0,
            isFocused,
        });
    }

    /*
     * Check whether anchor point of the range is at the start of a `node`.
     */
    public hasAnchorAtStartOf(node: NodeType): boolean {
        // PERF: Do a check for a `0` offset first since it's quickest.
        if (this.anchorOffset !== 0) {
            return false;
        }
        const first = getFirst(node);
        return first ? this.anchorKey === first.key : false;
    }

    /*
     * Check whether anchor point of the range is at the end of a `node`.
     */
    public hasAnchorAtEndOf(node: NodeType): boolean {
        const last = getLast(node);
        return last ? this.anchorKey === last.key && this.anchorOffset === last.text.length : false;
    }

    /*
     * Check whether the anchor edge of a range is in a `node` and at an
     * offset between `start` and `end`.
     */
    public hasAnchorBetween(node: NodeType, start: number, end: number): boolean {
        return this.anchorOffset <= end && start <= this.anchorOffset && this.hasAnchorIn(node);
    }

    /*
     * Check whether the anchor edge of a range is in a `node`.
     */
    public hasAnchorIn(node: NodeType): boolean {
        return node.object === 'text'
            ? node.key === this.anchorKey
            : this.anchorKey != null && (node as Block).hasDescendant(this.anchorKey);
    }

    /*
     * Check whether focus point of the range is at the end of a `node`.
     */
    public hasFocusAtEndOf(node: NodeType): boolean {
        const last = getLast(node);
        return last ? this.focusKey === last.key && this.focusOffset === last.text.length : false;
    }

    /*
     * Check whether focus point of the range is at the start of a `node`.
     */
    public hasFocusAtStartOf(node: NodeType): boolean {
        if (this.focusOffset !== 0) {
            return false;
        }
        const first = getFirst(node);
        return first ? this.focusKey === first.key : false;
    }

    /*
     * Check whether the focus edge of a range is in a `node` and at an
     * offset between `start` and `end`.
     */
    public hasFocusBetween(node: NodeType, start: number, end: number): boolean {
        return start <= this.focusOffset && this.focusOffset <= end && this.hasFocusIn(node);
    }

    /**
     * Check whether the focus edge of a range is in a `node`.
     */
    public hasFocusIn(node: NodeType): boolean {
        return node.object === 'text'
            ? node.key === this.focusKey
            : this.focusKey != null && (node as Block).hasDescendant(this.focusKey);
    }

    /**
     * Check whether a node is closely wrapped by selection
     */
    public isOnlyNodeWrapped(node: NodeType, parent: NodeType): boolean {
        const isInlineOrBlock = isInline(node) || isBlock(node);
        if (!isBlock(parent) || !isInlineOrBlock) {
            return false;
        }
        const previousSibling = parent.getPreviousSibling(node.key);
        const nextSibling = parent.getNextSibling(node.key);
        const neighbourSelection = {
            previous: {
                isStart: this.startKey === previousSibling?.key,
                isEdge: Boolean(previousSibling && this.hasStartAtEndOf(previousSibling)),
            },
            next: {
                isEnd: this.endKey === nextSibling?.key,
                isEdge: Boolean(nextSibling && this.hasEndAtStartOf(nextSibling)),
            },
        };
        return (
            neighbourSelection.previous.isStart &&
            neighbourSelection.previous.isEdge &&
            neighbourSelection.next.isEnd &&
            neighbourSelection.next.isEdge
        );
    }

    /**
     * Check if the cursor is only in a node.
     */
    public hasOnlyNodeSelected(node: Inline | Block): boolean {
        return this.isCollapsed && this.hasFocusIn(node);
    }
    /**
     * Check if the cursor is only in a node and the node is expanded.
     */
    public hasOnlyNodeSelectedExpanded(node: NodeType): boolean {
        // can rely on hasOnlyNodeSelected if the selection is collapsed
        if (this.isCollapsed || !this.focusKey || !this.anchorKey) {
            return false;
        }
        return this.hasFocusIn(node) && this.hasAnchorIn(node);
    }
    /**
     * True if the cursor is active inside the given node (and focused).
     */
    public hasOnlyNodeFocused(node: Inline | Block): boolean {
        return Boolean(this.isFocused) && this.hasOnlyNodeSelected(node);
    }

    /**
     * Check if the selection is in a fragment of a void node.
     */
    public hasFocusInFragmentOf(node: Block): Fragment | null {
        if (!this.hasFocusIn(node)) {
            return null;
        }

        const ancestors = node.getAncestors(this.startKey!);
        if (!ancestors) {
            return null;
        }

        const fragment = ancestors.find((ancestor) => ancestor.object === 'fragment') as
            | Fragment
            | undefined;
        return fragment || null;
    }

    /*
     * Check whether the range is at the start of a `node`.
     */
    public isAtStartOf(node: NodeType): boolean {
        return this.isCollapsed && this.hasAnchorAtStartOf(node);
    }

    /*
     * Check whether the range is at the end of a `node`.
     */
    public isAtEndOf(node: NodeType): boolean {
        return this.isCollapsed && this.hasAnchorAtEndOf(node);
    }

    /*
     * Focus the range.
     */
    public focus(): TextRange {
        return this.merge({
            isFocused: true,
        });
    }

    /*
     * Blur the range.
     */

    public blur(): TextRange {
        return this.merge({
            isFocused: false,
        });
    }

    /*
     * Unset the range.
     */
    public deselect(): TextRange {
        return this.merge({
            anchorKey: null,
            anchorOffset: 0,
            focusKey: null,
            focusOffset: 0,
            isFocused: false,
            isBackward: false,
            data: Data.create(),
        });
    }

    /*
     * Flip the range.
     */
    public flip(): TextRange {
        return this.merge({
            anchorKey: this.focusKey,
            anchorOffset: this.focusOffset,
            focusKey: this.anchorKey,
            focusOffset: this.anchorOffset,
            isBackward: this.isBackward == null ? null : !this.isBackward,
        });
    }

    /*
     * Move the anchor offset `n` characters.
     */
    public moveAnchor(n = 1): TextRange {
        const { anchorKey, focusKey, focusOffset, isBackward } = this;
        const anchorOffset = this.anchorOffset + n;
        return this.merge({
            anchorOffset,
            isBackward: anchorKey === focusKey ? anchorOffset > focusOffset : isBackward,
        });
    }

    /*
     * Move the anchor offset `n` characters.
     */
    public moveFocus(n = 1): TextRange {
        const { anchorKey, anchorOffset, focusKey, isBackward } = this;
        const focusOffset = this.focusOffset + n;
        return this.merge({
            focusOffset,
            isBackward: focusKey === anchorKey ? anchorOffset > focusOffset : isBackward,
        });
    }

    /*
     * Move the range's anchor point to a `key` and `offset`.
     */

    public moveAnchorTo(key: string, offset?: number): TextRange {
        const { anchorKey, focusKey, focusOffset, isBackward } = this;
        return this.merge({
            anchorKey: key,
            anchorOffset: offset,
            isBackward:
                key === focusKey ? offset! > focusOffset : key === anchorKey ? isBackward : null,
        });
    }

    /*
     * Move the range's focus point to a `key` and `offset`.
     */
    public moveFocusTo(key: string, offset?: number): TextRange {
        const { focusKey, anchorKey, anchorOffset, isBackward } = this;
        return this.merge({
            focusKey: key,
            focusOffset: offset,
            isBackward:
                key === anchorKey ? anchorOffset > offset! : key === focusKey ? isBackward : null,
        });
    }

    /*
     * Move the range to `anchorOffset`.
     */
    public moveAnchorOffsetTo(anchorOffset: number): TextRange {
        return this.merge({
            anchorOffset,
            isBackward:
                this.anchorKey === this.focusKey
                    ? anchorOffset > this.focusOffset
                    : this.isBackward,
        });
    }

    /*
     * Move the range to `focusOffset`.
     */
    public moveFocusOffsetTo(focusOffset: number): TextRange {
        return this.merge({
            focusOffset,
            isBackward:
                this.anchorKey === this.focusKey
                    ? this.anchorOffset > focusOffset
                    : this.isBackward,
        });
    }

    /*
     * Move the range to `anchorOffset` and `focusOffset`.
     */
    public moveOffsetsTo(anchorOffset: number, focusOffset: number = anchorOffset): TextRange {
        return this.moveAnchorOffsetTo(anchorOffset).moveFocusOffsetTo(focusOffset);
    }

    /*
     * Move the focus point to the anchor point.
     */

    public moveToAnchor(): TextRange {
        return this.moveFocusTo(this.anchorKey!, this.anchorOffset);
    }

    /*
     * Move the anchor point to the focus point.
     */
    public moveToFocus(): TextRange {
        return this.moveAnchorTo(this.focusKey!, this.focusOffset);
    }

    /*
     * Move the range's anchor point to the start of a `node`.
     */
    public moveAnchorToStartOf(node: NodeType): TextRange {
        node = getFirst(node)!;
        return this.moveAnchorTo(node.key, 0);
    }

    /*
     * Move the range's anchor point to the end of a `node`.
     */
    public moveAnchorToEndOf(node: NodeType): TextRange {
        node = getLast(node)!;
        return this.moveAnchorTo(node.key, node.text.length);
    }

    /*
     * Move the range's focus point to the start of a `node`.
     */
    public moveFocusToStartOf(node: NodeType): TextRange {
        node = getFirst(node)!;
        return this.moveFocusTo(node.key, 0);
    }

    /*
     * Move the range's focus point to the end of a `node`.
     */
    public moveFocusToEndOf(node: NodeType): TextRange {
        node = getLast(node)!;
        return this.moveFocusTo(node.key, node.text.length);
    }

    /*
     * Move to the entire range of `start` and `end` nodes.
     */
    public moveToRangeOf(start: NodeType, end: NodeType = start): TextRange {
        const range = this.isBackward ? this.flip() : this;
        return range.moveAnchorToStartOf(start).moveFocusToEndOf(end);
    }

    /*
     * Normalize the range, relative to a `node`, ensuring that the anchor
     * and focus nodes of the range always refer to leaf text nodes.
     */
    public normalize(node: Document): TextRange {
        let { anchorOffset, focusOffset, isBackward } = this;
        const { anchorKey, focusKey } = this;

        const anchorOffsetType = typeof anchorOffset;
        const focusOffsetType = typeof focusOffset;

        if (anchorOffsetType !== 'number' || focusOffsetType !== 'number') {
            logger.warning(
                `The range offsets should be numbers, but they were of type "${anchorOffsetType}" and "${focusOffsetType}".`
            );
        }

        // If the range is unset, make sure it is properly zeroed out.
        if (anchorKey == null || focusKey == null) {
            return this.merge({
                anchorKey: null,
                anchorOffset: 0,
                focusKey: null,
                focusOffset: 0,
                isBackward: false,
            });
        }

        // Get the potential fragment ancestor
        if (anchorKey !== focusKey) {
            const anchorFragment = node.getClosestFragment(anchorKey);
            const focusFragment = node.getClosestFragment(focusKey);

            if (anchorFragment && !focusFragment) {
                const anchorOutsideFragment = node.getClosestNonFragment(anchorKey);
                if (anchorOutsideFragment) {
                    return this.isBackward
                        ? this.moveAnchorToStartOf(anchorOutsideFragment)
                        : this.moveAnchorToEndOf(anchorOutsideFragment);
                }
            }

            // If focus is in a fragment, but not the anchor
            // we move the focus to be outside the fragment.
            if (!anchorFragment && focusFragment) {
                const focusOutsideFragment = node.getClosestNonFragment(focusKey);
                if (focusOutsideFragment) {
                    return this.isBackward
                        ? this.moveFocusToStartOf(focusOutsideFragment)
                        : this.moveFocusToEndOf(focusOutsideFragment);
                }
            }

            if (anchorFragment !== focusFragment) {
                const anchorOutsideFragment = node.getClosestNonFragment(anchorKey);
                const focusOutsideFragment = node.getClosestNonFragment(focusKey);

                if (anchorOutsideFragment && focusOutsideFragment) {
                    return this.isBackward
                        ? this.moveAnchorToStartOf(anchorOutsideFragment).moveFocusToStartOf(
                              focusOutsideFragment
                          )
                        : this.moveAnchorToEndOf(anchorOutsideFragment).moveFocusToEndOf(
                              focusOutsideFragment
                          );
                }
                return this.deselect();
            }
        }

        // Get the anchor and focus nodes.
        let anchorNode = node.getDescendant(anchorKey);
        let focusNode = node.getDescendant(focusKey);

        // If the range is malformed, warn and zero it out.
        if (!anchorNode || !focusNode) {
            logger.warning('The range was invalid and was reset. The range in question was:', this);

            const first = node.getFirstText();
            return this.merge({
                anchorKey: first ? first.key : null,
                anchorOffset: 0,
                focusKey: first ? first.key : null,
                focusOffset: 0,
                isBackward: false,
            });
        }

        // If the anchor node isn't a text node, match it to one.
        if (anchorNode.object !== 'text') {
            logger.warning(
                'The range anchor was set to a Node that is not a Text node. This should not happen and can degrade performance. The node in question was:',
                anchorNode
            );

            const nextAnchorNode = anchorNode.getTextAtOffset(anchorOffset)!;
            const offset = anchorNode.getOffset(nextAnchorNode.key);
            anchorOffset -= offset;
            anchorNode = nextAnchorNode;
        }

        // If the focus node isn't a text node, match it to one.
        if (focusNode.object !== 'text') {
            logger.warning(
                'The range focus was set to a Node that is not a Text node. This should not happen and can degrade performance. The node in question was:',
                focusNode
            );

            const nextFocusNode = focusNode.getTextAtOffset(focusOffset)!;
            const offset = focusNode.getOffset(nextFocusNode.key);
            focusOffset -= offset;
            focusNode = nextFocusNode;
        }

        // If `isBackward` is not set, derive it.
        if (isBackward == null) {
            if (anchorNode.key === focusNode.key) {
                isBackward = anchorOffset > focusOffset;
            } else {
                isBackward = !node.areDescendantsSorted(anchorNode.key, focusNode.key);
            }
        }
        const anchorText = node.getDescendant(anchorKey)!;
        const focusText = node.getDescendant(focusKey)!;

        // Normalize offsets to be inside their respective text
        anchorOffset = clamp(anchorOffset, 0, anchorText.text.length);
        focusOffset = clamp(focusOffset, 0, focusText.text.length);

        if (this.isExpanded) {
            const anchorParent = anchorNode ? node.getParent(anchorNode.key) : null;
            const focusParent = focusNode ? node.getParent(focusNode.key) : null;
            const isAnchorParentInlineVoid = anchorParent?.isInline() && anchorParent.isVoid;
            const isFocusParentInlineVoid = focusParent?.isInline() && focusParent.isVoid;

            // Anchor is inside an inline void
            if (isAnchorParentInlineVoid) {
                if (focusNode?.isText() && focusNode.isEmpty) {
                    // Collapse the selection into the inline void as the focus is in an empty text node
                    return this.merge({
                        anchorKey: anchorNode?.key,
                        anchorOffset: 0,
                        focusKey: anchorNode?.key,
                        focusOffset: 0,
                        isBackward,
                    });
                }
                // Properly wrap the inline void by moving the anchor inside the text node adjacent to the inline void
                const adjacent = isBackward
                    ? node.getNextTextAfterNode(anchorParent.key)
                    : node.getPreviousTextBeforeNode(anchorParent.key);

                if (adjacent) {
                    return this.merge({
                        anchorKey: adjacent.key,
                        anchorOffset: isBackward ? 0 : adjacent.text.length,
                        focusKey: focusNode?.key,
                        focusOffset,
                        isBackward,
                    });
                }
            }

            // Focus is inside an inline void
            if (isFocusParentInlineVoid) {
                if (anchorNode?.isText() && anchorNode.isEmpty) {
                    // Collapse the selection into the inline void as the anchor is in an empty text node
                    return this.merge({
                        anchorKey: focusNode?.key,
                        anchorOffset: 0,
                        focusKey: focusNode?.key,
                        focusOffset: 0,
                        isBackward,
                    });
                }
                // Properly wrap the inline void by moving the focus inside the text node adjacent to the inline void
                const adjacent = isBackward
                    ? node.getPreviousTextBeforeNode(focusParent.key)
                    : node.getNextTextAfterNode(focusParent.key);

                if (adjacent) {
                    return this.merge({
                        anchorKey: anchorNode?.key,
                        anchorOffset,
                        focusKey: adjacent.key,
                        focusOffset: isBackward ? adjacent.text.length : 0,
                        isBackward,
                    });
                }
            }
        }

        return this.merge({
            anchorKey: anchorNode.key,
            anchorOffset,
            focusKey: focusNode.key,
            focusOffset,
            isBackward,
        });
    }

    /**
     * Normalize the range to be a valid user selection in a document.
     * It differs from `normalize` in that it will enforce more rules to ensure
     * the range is a valid user selection.
     */
    public normalizeSelection(document: Document): TextRange {
        const { anchorKey, focusKey, anchorOffset, focusOffset } = this;

        if (!anchorKey || !focusKey) {
            return this;
        }

        const anchorInline = document.getClosestInline(anchorKey);
        const focusInline = document.getClosestInline(focusKey);
        const focusBlock = document.getClosestBlock(focusKey);
        const anchorBlock = document.getClosestBlock(anchorKey);

        let result: TextRange = this;

        // COMPAT: If the anchor point is at the start of a non-void, and the
        // focus point is inside a void node with an offset that isn't `0`, set
        // the focus offset to `0`. This is due to void nodes <span>'s being
        // positioned off screen, resulting in the offset always being greater
        // than `0`. Since we can't know what it really should be, and since an
        // offset of `0` is less destructive because it creates a hanging
        // selection, go with `0`. (2017/09/07)
        if (
            anchorBlock &&
            !anchorBlock.isVoid &&
            anchorOffset === 0 &&
            focusBlock &&
            focusBlock.isVoid &&
            focusOffset !== 0
        ) {
            result = result.set('focusOffset', 0);
        }

        // If the selection is at the start of a non-void inline node,and
        // there is a node before it, put it in the node before instead. This
        // standardizes the behavior, since it's indistinguishable to the user.
        if (anchorInline && !anchorInline.isVoid && result.hasAnchorAtStartOf(anchorInline)) {
            const previous = document.getPreviousTextBeforeNode(anchorKey, false);
            if (previous) {
                result = result.merge({
                    anchorKey: previous.key,
                    anchorOffset: previous.text.length,
                });
            }
        }
        if (focusInline && !focusInline.isVoid && result.hasFocusAtStartOf(focusInline)) {
            const previous = document.getPreviousTextBeforeNode(focusKey, false);
            if (previous) {
                result = result.merge({
                    focusKey: previous.key,
                    focusOffset: previous.text.length,
                });
            }
        }

        // COMPAT: If the selection is at the end of a non-void inline node, and
        // there is a node after it, put it in the node after instead. This
        // standardizes the behavior, since it's indistinguishable to the user.
        if (anchorInline && !anchorInline.isVoid && result.hasAnchorAtEndOf(anchorInline)) {
            const next = document.getNextTextAfterNode(anchorKey);
            if (next) {
                result = result.merge({
                    anchorKey: next.key,
                    anchorOffset: 0,
                });
            }
        }
        if (focusInline && !focusInline.isVoid && result.hasFocusAtEndOf(focusInline)) {
            const next = document.getNextTextAfterNode(focusKey);
            if (next) {
                result = result.merge({
                    focusKey: next.key,
                    focusOffset: 0,
                });
            }
        }

        return result;
    }

    /**
     * Compare if two ranges are at the same position.
     */
    public hasMoved(b: SelectionRange): boolean {
        if (!isTextRange(b)) {
            return true;
        }
        return !this.merge({
            isFocused: false,
            isBackward: false,
            marks: null,
        }).equals(
            b.merge({
                isFocused: false,
                isBackward: false,
                marks: null,
            })
        );
    }

    // methods added by modifying the TextRange prototype

    declare public move: (...args: Parameters<TextRange['moveAnchor']>) => TextRange;
    declare public moveTo: (...args: Parameters<TextRange['moveAnchorTo']>) => TextRange;
    declare public moveToStartOf: (
        ...args: Parameters<TextRange['moveAnchorToStartOf']>
    ) => TextRange;
    declare public moveToEndOf: (...args: Parameters<TextRange['moveAnchorToEndOf']>) => TextRange;

    declare public hasStartAtStartOf: (
        ...args: Parameters<TextRange['hasAnchorAtStartOf']>
    ) => boolean;
    declare public hasEndAtStartOf: (
        ...args: Parameters<TextRange['hasFocusAtStartOf']>
    ) => boolean;
    declare public hasEdgeAtStartOf: (
        ...args: Parameters<TextRange['hasAnchorAtStartOf']>
    ) => boolean;
    declare public hasStartAtEndOf: (...args: Parameters<TextRange['hasAnchorAtEndOf']>) => boolean;
    declare public hasEndAtEndOf: (...args: Parameters<TextRange['hasFocusAtEndOf']>) => boolean;
    declare public hasEdgeAtEndOf: (...args: Parameters<TextRange['hasAnchorAtEndOf']>) => boolean;
    declare public hasStartBetween: (...args: Parameters<TextRange['hasAnchorBetween']>) => boolean;
    declare public hasEndBetween: (...args: Parameters<TextRange['hasFocusBetween']>) => boolean;
    declare public hasEdgeBetween: (...args: Parameters<TextRange['hasAnchorBetween']>) => boolean;
    declare public hasStartIn: (...args: Parameters<TextRange['hasAnchorIn']>) => boolean;
    declare public hasEndAtIn: (...args: Parameters<TextRange['hasFocusIn']>) => boolean;
    declare public hasEdgeIn: (...args: Parameters<TextRange['hasAnchorIn']>) => boolean;
    declare public moveStart: (...args: Parameters<TextRange['moveAnchor']>) => boolean;
    declare public moveEnd: (...args: Parameters<TextRange['moveFocus']>) => TextRange;
    declare public moveToStart: (...args: Parameters<TextRange['moveToAnchor']>) => TextRange;
    declare public moveToEnd: (...args: Parameters<TextRange['moveToFocus']>) => TextRange;
    declare public moveStartTo: (...args: Parameters<TextRange['moveAnchorTo']>) => TextRange;
    declare public moveEndTo: (...args: Parameters<TextRange['moveFocusTo']>) => TextRange;
    declare public moveStartOffsetTo: (
        ...args: Parameters<TextRange['moveAnchorOffsetTo']>
    ) => TextRange;
    declare public moveEndOffsetTo: (
        ...args: Parameters<TextRange['moveFocusOffsetTo']>
    ) => TextRange;

    declare public collapseTo: (...args: Parameters<TextRange['moveTo']>) => TextRange;
    declare public collapseToAnchor: (...args: Parameters<TextRange['moveToAnchor']>) => TextRange;
    declare public collapseToFocus: (...args: Parameters<TextRange['moveToFocus']>) => TextRange;
    declare public collapseToStart: (...args: Parameters<TextRange['moveToStart']>) => TextRange;
    declare public collapseToEnd: (...args: Parameters<TextRange['moveToEnd']>) => TextRange;
    declare public collapseToStartOf: (
        ...args: Parameters<TextRange['moveToStartOf']>
    ) => TextRange;
    declare public extend: (...args: Parameters<TextRange['moveFocus']>) => TextRange;
    declare public extendTo: (...args: Parameters<TextRange['moveFocusTo']>) => TextRange;
    declare public extendToStartOf: (
        ...args: Parameters<TextRange['moveFocusToStartOf']>
    ) => TextRange;
    declare public extendToEndOf: (...args: Parameters<TextRange['moveFocusToEndOf']>) => TextRange;
    declare public collapseToEndOf: (
        ...args: Parameters<TextRange['moveFocusToEndOf']>
    ) => TextRange;
}

/*
 * Attach a pseudo-symbol for type checking.
 */

TextRange.prototype[MODEL_TYPES.TEXT_RANGE] = true;

/*
 * Mix in some "move" convenience methods.
 */

const MOVE_METHODS = [
    ['move', ''],
    ['move', 'To'],
    ['move', 'ToStartOf'],
    ['move', 'ToEndOf'],
];

MOVE_METHODS.forEach(([p, s]) => {
    TextRange.prototype[`${p}${s}`] = function (...args) {
        return this[`${p}Anchor${s}`](...args)[`${p}Focus${s}`](...args);
    };
});

/*
 * Mix in the "start", "end" and "edge" convenience methods.
 */

const EDGE_METHODS = [
    ['has', 'AtStartOf', true],
    ['has', 'AtEndOf', true],
    ['has', 'Between', true],
    ['has', 'In', true],
    ['collapseTo', ''],
    ['move', ''],
    ['moveTo', ''],
    ['move', 'To'],
    ['move', 'OffsetTo'],
];

EDGE_METHODS.forEach(([p, s, hasEdge]) => {
    const anchor = `${p}Anchor${s}`;
    const focus = `${p}Focus${s}`;

    TextRange.prototype[`${p}Start${s}`] = function (...args) {
        return this.isBackward ? this[focus](...args) : this[anchor](...args);
    };

    TextRange.prototype[`${p}End${s}`] = function (...args) {
        return this.isBackward ? this[anchor](...args) : this[focus](...args);
    };

    if (hasEdge) {
        TextRange.prototype[`${p}Edge${s}`] = function (...args) {
            return this[anchor](...args) || this[focus](...args);
        };
    }
});

/*
 * Mix in some aliases for convenience / parallelism with the browser APIs.
 */

const ALIAS_METHODS = [
    ['collapseTo', 'moveTo'],
    ['collapseToAnchor', 'moveToAnchor'],
    ['collapseToFocus', 'moveToFocus'],
    ['collapseToStart', 'moveToStart'],
    ['collapseToEnd', 'moveToEnd'],
    ['collapseToStartOf', 'moveToStartOf'],
    ['collapseToEndOf', 'moveToEndOf'],
    ['extend', 'moveFocus'],
    ['extendTo', 'moveFocusTo'],
    ['extendToStartOf', 'moveFocusToStartOf'],
    ['extendToEndOf', 'moveFocusToEndOf'],
];

ALIAS_METHODS.forEach(([alias, method]) => {
    TextRange.prototype[alias] = function (...args) {
        return this[method](...args);
    };
});

/*
 * Get the first text of a `node`.
 */
function getFirst(node: NodeType): Text | null {
    return node.object === 'text' ? node : 'getFirstText' in node ? node.getFirstText() : null;
}

/*
 * Get the last text of a `node`.
 */
function getLast(node: NodeType): Text | null {
    return node.object === 'text' ? node : 'getLastText' in node ? node.getLastText() : null;
}
